import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import { casePath, equipmentPath, gpsDevicePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface EquipmentAssignment {
  id: string
  entry_type: "equipment" | "gps"
  equipment_id: number
  equipment_name: string | null
  equipment_type: string | null
  assigned_at: string | null
  employee_name: string | null
  case_id: number | null
  case_name: string | null
}

interface EquipmentHistoryEntry extends EquipmentAssignment {
  returned_at: string | null
}

interface FilterOption {
  id: number
  name: string
}

interface Filters {
  employee_id: string | null
  equipment_type_id: string | null
  start_date: string | null
  end_date: string | null
}

interface FilterOptions {
  employees: FilterOption[]
  equipment_types: FilterOption[]
}

interface Props {
  assignments: EquipmentAssignment[]
  history: EquipmentHistoryEntry[]
  filters: Filters
  filter_options: FilterOptions
  viewing_employee: string | null
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

function equipmentDetailPath(entry: EquipmentAssignment): string {
  if (entry.entry_type === "gps") {
    return gpsDevicePath(entry.equipment_id)
  }
  return equipmentPath(entry.equipment_id)
}

/* -- Constants -------------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Activity", href: "#" },
  { title: "Equipment", href: "/employees/activity/equipment" },
]

/* -- Component -------------------------------------------- */

export default function ActivityEquipment({
  assignments,
  history,
  filters,
  filter_options,
  viewing_employee,
}: Props) {
  const [employeeId, setEmployeeId] = useState(filters.employee_id ?? "")
  const [equipmentTypeId, setEquipmentTypeId] = useState(
    filters.equipment_type_id ?? "",
  )
  const [startDate, setStartDate] = useState(filters.start_date ?? "")
  const [endDate, setEndDate] = useState(filters.end_date ?? "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    const params: Record<string, string> = {}
    if (employeeId) params.employee_id = employeeId
    if (equipmentTypeId) params.equipment_type_id = equipmentTypeId
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate

    router.get("/employees/activity/equipment", params, {
      preserveState: true,
    })
  }

  const title = viewing_employee
    ? `${viewing_employee} - Currently Assigned Equipment`
    : "Currently Assigned Equipment"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Equipment Activity" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">{title}</h1>

        {/* Filter Form */}
        <div className="border-hpi-light-gray mb-6 rounded border bg-gray-50 p-4">
          <form
            onSubmit={handleSearch}
            className="flex flex-wrap items-end gap-4"
          >
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Employee
              </label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {filter_options.employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Type
              </label>
              <select
                value={equipmentTypeId}
                onChange={(e) => setEquipmentTypeId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {filter_options.equipment_types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                From
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                To
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <button
                type="submit"
                className="bg-hpi-blue rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Results */}
        {assignments.length === 0 ? (
          <div className="rounded border border-red-200 bg-red-50 px-6 py-8 text-center">
            <p className="text-lg font-medium text-red-800">
              No equipment currently assigned
            </p>
          </div>
        ) : (
          <div className="border-hpi-light-gray overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray text-hpi-med-gray border-b bg-gray-50 text-left text-xs font-medium tracking-wider uppercase">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Assigned To</th>
                </tr>
              </thead>
              <tbody className="divide-hpi-light-gray divide-y bg-white">
                {assignments.map((entry) => (
                  <AssignmentRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Assignment History */}
        <h2 className="mt-8 mb-4 text-xl font-semibold">Assignment History</h2>

        {history.length === 0 ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-6 py-8 text-center">
            <p className="text-hpi-med-gray text-sm">
              No equipment assignment history found
            </p>
          </div>
        ) : (
          <div className="border-hpi-light-gray overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray text-hpi-med-gray border-b bg-gray-50 text-left text-xs font-medium tracking-wider uppercase">
                  <th className="px-4 py-2">From</th>
                  <th className="px-4 py-2">To</th>
                  <th className="px-4 py-2">Who</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Device</th>
                  <th className="px-4 py-2">Case</th>
                </tr>
              </thead>
              <tbody className="divide-hpi-light-gray divide-y bg-white">
                {history.map((entry) => (
                  <HistoryRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

/* -- Row -------------------------------------------------- */

function AssignmentRow({ entry }: { entry: EquipmentAssignment }) {
  const detailHref = equipmentDetailPath(entry)

  return (
    <tr className="hover:bg-hpi-pale-blue">
      <td className="px-4 py-2 whitespace-nowrap">
        <a href={detailHref} className="text-hpi-blue hover:underline">
          {formatDate(entry.assigned_at)}
        </a>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <a href={detailHref} className="text-hpi-blue hover:underline">
          {entry.equipment_type ?? "-"}
        </a>
      </td>
      <td className="px-4 py-2">
        <a href={detailHref} className="text-hpi-blue hover:underline">
          {entry.equipment_name ?? "-"}
        </a>
        {entry.case_id && entry.case_name && (
          <span className="text-hpi-med-gray ml-1">
            (
            <a
              href={casePath(entry.case_id)}
              className="text-hpi-blue hover:underline"
            >
              {entry.case_name}
            </a>
            )
          </span>
        )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {entry.employee_name ?? "-"}
      </td>
    </tr>
  )
}

/* -- History Row ------------------------------------------ */

function HistoryRow({ entry }: { entry: EquipmentHistoryEntry }) {
  const detailHref = equipmentDetailPath(entry)

  return (
    <tr className="hover:bg-hpi-pale-blue">
      <td className="px-4 py-2 whitespace-nowrap">
        {formatDate(entry.assigned_at)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {formatDate(entry.returned_at)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {entry.employee_name ?? "-"}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {entry.equipment_type ?? "-"}
      </td>
      <td className="px-4 py-2">
        <a href={detailHref} className="text-hpi-blue hover:underline">
          {entry.equipment_name ?? "-"}
        </a>
      </td>
      <td className="px-4 py-2">
        {entry.case_id && entry.case_name ? (
          <a
            href={casePath(entry.case_id)}
            className="text-hpi-blue hover:underline"
          >
            {entry.case_name}
          </a>
        ) : null}
      </td>
    </tr>
  )
}
